import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleSectionInView = () => {
      const sections = ['home', 'about', 'leadership', 'activities', 'quality', 'careers', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleSectionInView);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleSectionInView);
    };
  }, []);

  const navItems = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.leadership'), href: '#leadership' },
    { name: t('nav.activities'), href: '#activities' },
    { name: t('nav.quality'), href: '#quality' },
    { name: t('nav.careers'), href: '#careers' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-background/80 backdrop-blur-lg border-b border-border/20 shadow-lg' 
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <motion.div 
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex-shrink-0">
              <motion.div 
                className="relative overflow-hidden rounded-lg"
                whileHover={{ rotate: [0, -2, 2, 0], transition: { duration: 0.5 }}}
              >
                <img 
                  src="/Black_Logo.jpg" 
                  alt="IAC Logo" 
                  className="h-12 w-auto object-contain"
                />
              </motion.div>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-1">
              {navItems.map((item, index) => {
                const sectionId = item.href.replace('#', '');
                const isActive = item.isRoute
                  ? location.pathname === item.href
                  : activeSection === sectionId;

                const LinkComponent = item.isRoute ? Link : 'a';
                const linkProps = item.isRoute
                  ? { to: item.href }
                  : { href: item.href };

                return (
                  <motion.div
                    key={item.name}
                    whileHover={{
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <LinkComponent
                      {...linkProps}
                      className={`relative px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 group ${
                        isActive
                          ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg shadow-blue-500/25 ring-2 ring-offset-2 ring-blue-500 ring-offset-background'
                          : 'text-gray-800 hover:text-white bg-white/50 hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 border border-gray-300 hover:border-blue-500 shadow-sm hover:shadow-lg'
                      }`}
                    >
                      <span className="relative z-10">{item.name}</span>

                      {/* Background highlight on hover */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300"
                        initial={{ scale: 0.8, opacity: 0 }}
                        whileHover={{ scale: 1, opacity: 1 }}
                      />

                      {/* Animated underline - high contrast */}
                      <motion.div
                        className="absolute -bottom-1 left-1/2 w-0 h-1.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full shadow-md"
                        initial={{ x: '-50%', opacity: 0 }}
                        whileHover={{ width: '100%', opacity: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      />

                      {/* Active indicator - very visible with ring */}
                      {isActive && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg shadow-blue-500/30 ring-2 ring-offset-2 ring-blue-500 ring-offset-background"
                          layoutId="activeSection"
                          transition={{ type: "spring", bounce: 0.3, duration: 0.6 }}
                        />
                      )}

                      {/* High contrast shadow on hover */}
                      <motion.div
                        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100"
                        whileHover={{
                          scale: 1.1,
                          boxShadow: '0 4px 20px rgba(59, 130, 246, 0.5), 0 8px 40px rgba(168, 85, 247, 0.3)'
                        }}
                        transition={{ duration: 0.3 }}
                      />
                    </LinkComponent>
                  </motion.div>
                );
              })}
              <div className="ml-4 flex items-center space-x-2">
                <ThemeToggle />
                <LanguageSwitcher />
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg hover:bg-white/10 transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6 text-foreground" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6 text-foreground" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="md:hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-2 pt-2 pb-3 space-y-2 sm:px-3 bg-background/80 backdrop-blur-lg border-t border-border/20">
                {navItems.map((item, index) => {
                  const sectionId = item.href.replace('#', '');
                  const isActive = item.isRoute
                    ? location.pathname === item.href
                    : activeSection === sectionId;

                  const LinkComponent = item.isRoute ? Link : 'a';
                  const linkProps = item.isRoute
                    ? { to: item.href, onClick: () => setIsMenuOpen(false) }
                    : { href: item.href, onClick: () => setIsMenuOpen(false) };

                  return (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <LinkComponent
                        {...linkProps}
                        className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 group ${
                          isActive
                            ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg shadow-blue-500/25 border border-blue-500/30'
                            : 'text-gray-800 hover:text-white bg-gray-100 hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 border border-gray-300 hover:border-blue-500 shadow-sm hover:shadow-lg'
                        }`}
                      >
                        {item.name}
                      </LinkComponent>
                    </motion.div>
                  );
                })}
                <div className="px-3 py-2 space-y-2">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navItems.length * 0.1, duration: 0.3 }}
                  >
                    <ThemeToggle />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navItems.length * 0.1 + 0.1, duration: 0.3 }}
                  >
                    <LanguageSwitcher />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;